import { Box, Stack, Text, Title, SimpleGrid, Button } from "@mantine/core";
import { Link, useOutletContext } from "react-router-dom";
import type { PrivateContext } from "@/components/layout/PrivateLayout";
import type { AccessScope } from "@/content/types";

interface ScopeRoute {
  scope: AccessScope;
  to: string;
  title: string;
  body: string;
}

const SCOPE_ROUTES: ScopeRoute[] = [
  {
    scope: "unreleased-audio",
    to: "/private/unreleased",
    title: "Unreleased",
    body: "Forthcoming masters — SWEET OUTSIDE and other signed releases.",
  },
  {
    scope: "catalog-unreleased",
    to: "/private/vault",
    title: "Vault",
    body: "Broader catalog — unsigned-finished + demo tracks.",
  },
  {
    scope: "edits-mashups",
    to: "/private/edits",
    title: "Edits / DJ tools",
    body: "Mashups, bootlegs, unofficial edits. DJ-set material — not for redistribution.",
  },
  {
    scope: "raw-assets",
    to: "/private/assets",
    title: "Raw assets",
    body: "Hi-res photo, raw video, artwork originals.",
  },
];

export default function PrivateIndex() {
  const { session } = useOutletContext<PrivateContext>();
  const unlocked = SCOPE_ROUTES.filter((s) => session.scope.includes(s.scope));
  const locked = SCOPE_ROUTES.filter((s) => !session.scope.includes(s.scope));

  return (
    <Stack gap="lg">
      <Box>
        <Title order={1} className="iridescent-text">
          Private
        </Title>
        <Text c="dimmed" mt="xs">
          Your token unlocks {unlocked.length} of {SCOPE_ROUTES.length} sections.
        </Text>
      </Box>

      <Box>
        <Title order={2} size="h3" mb="sm">
          Available
        </Title>
        <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
          {unlocked.map((r) => (
            <ScopeCard key={r.scope} {...r} unlocked />
          ))}
        </SimpleGrid>
      </Box>

      {locked.length > 0 && (
        <Box>
          <Title order={2} size="h3" mb="sm">
            Not in your scope
          </Title>
          <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
            {locked.map((r) => (
              <ScopeCard key={r.scope} {...r} />
            ))}
          </SimpleGrid>
        </Box>
      )}
    </Stack>
  );
}

function ScopeCard({ to, title, body, unlocked = false }: ScopeRoute & { unlocked?: boolean }) {
  return (
    <Box
      p="md"
      style={{
        background: "var(--color-deep)",
        border: `1px solid ${unlocked ? "var(--accent-indigo)" : "var(--color-ocean)"}`,
        borderRadius: 12,
        opacity: unlocked ? 1 : 0.55,
      }}
    >
      <Text fw={600}>{title}</Text>
      <Text size="sm" c="dimmed" mt={4}>
        {body}
      </Text>
      <Button
        component={Link}
        to={to}
        mt="sm"
        size="xs"
        color="indigo"
        variant={unlocked ? "filled" : "light"}
        disabled={!unlocked}
      >
        {unlocked ? "Open" : "Locked"}
      </Button>
    </Box>
  );
}
